import { datadogRum } from '@datadog/browser-rum'
import { useEffect } from 'react'
import { generateDataDogContext } from '../../utils/dataDog'
import {
  getUnknownOrderStatuses,
  isRequiredMisauthored,
} from '../../utils/orderConfirmation'
import useAppSelector from '../useAppSelector'

/**
 * Reports an `orderConfirmation` block that is authored and does nothing.
 *
 * Extracted from `useOrderDetail` in COM-776 because the block acquired a
 * second consumer that quarter — `useOrderConfirmation`, the in-webview
 * create-and-confirm flow — and the guards were reachable from neither the
 * screen that now depends on them most nor any path a shopper takes before
 * Order Detail. A storefront that opted in and typo'd `successStatuses` got
 * precisely the failure described below, on the checkout CTA, with no record
 * anywhere unless a shopper independently went and opened an order.
 *
 * Called from both consumers rather than copied into the second: two `useEffect`
 * blocks reporting the same misconfiguration would drift, and the report text
 * is the only thing anyone debugging this ever sees.
 *
 * Idempotent per mount and cheap: both predicates are pure reads over a config
 * object that is usually absent, and they fire nothing in the common case.
 * Mounting both consumers at once therefore double-reports a genuinely broken
 * block, which is the right failure direction for a signal that is otherwise
 * silent.
 *
 * @param functionName the caller, so Datadog says which surface saw it.
 * @internal
 */
export const useOrderConfirmationConfigGuards = (functionName: string) => {
  const orderConfirmation = useAppSelector(
    (state) => state.defaultSlice.sessionData?.configuration?.orderConfirmation,
  )

  /**
   * A configured status that is not an `OrderStatus` member matches no order
   * that will ever exist, so the flow polls to its deadline and reports a
   * timeout that never happened. Nothing server-side rejects it — the block is
   * free-form JSON — so this log is the only place it surfaces.
   *
   * Reported, not thrown: the screen still renders the order, and taking a
   * working page down over a config typo would be the larger outage. Same
   * shape as the `hidePrices` and `packages.sort` misconfiguration reports in
   * `fetchConfig`.
   */
  useEffect(() => {
    const unknownStatuses = getUnknownOrderStatuses(orderConfirmation)
    if (!unknownStatuses.length) return

    const context = generateDataDogContext({
      title: 'orderConfirmation statuses misconfigured',
      extraInfo: { function: functionName, unknownStatuses },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error(
        `orderConfirmation names statuses that are not OrderStatus members: ${unknownStatuses.join(
          ', ',
        )}`,
      ),
      context,
    )
  }, [orderConfirmation, functionName])

  /**
   * The other way the block can be authored and do nothing.
   *
   * `required` is read as literally `true` and nothing else, which is right —
   * a quoted `"false"` must not opt a storefront in — but it makes the
   * likelier authoring slip invisible: write `required: "true"`, or leave
   * `required` out while authoring `successStatuses`, `pollingTime` and
   * `timeout`, and the whole block resolves to `isAsync: false`. The shopper
   * then sees a still-`PROCESSING` order rendered as final, and the storefront
   * that configured async confirmation has no way to tell it did not take.
   *
   * Its own effect rather than a second branch of the one above because the
   * two conditions are independent: a block can be wrong in both ways at once,
   * and each is a different fix.
   */
  useEffect(() => {
    if (!isRequiredMisauthored(orderConfirmation)) return

    const context = generateDataDogContext({
      title: 'orderConfirmation.required misconfigured',
      extraInfo: {
        function: functionName,
        // Stringified so `undefined`, `"true"` and `0` are distinguishable in
        // Datadog rather than all arriving as an absent field.
        required: JSON.stringify(orderConfirmation?.required ?? null),
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error(
        'orderConfirmation is authored but `required` is not literally true; the confirming and timeout states stay off',
      ),
      context,
    )
  }, [orderConfirmation, functionName])
}

export default useOrderConfirmationConfigGuards
